import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '@/utils/index.ts'

type RentalResourceSelectionListProps<T extends { id: number }> = {
  title: string
  titleExtra?: ReactNode
  searchLabel: string
  searchPlaceholder: string
  items: T[]
  selectedId?: number
  keyword: string
  loading: boolean
  error?: string
  emptyText: string
  total: number
  hasMore: boolean
  onLoadMore: () => void
  resetKey: string
  getLabel: (item: T) => string
  renderPrimary: (item: T) => ReactNode
  renderSecondary?: (item: T) => ReactNode
  onKeywordChange: (keyword: string) => void
  onSearch: () => void
  onRetry?: () => void
  onSelect: (item: T) => void
}

export function RentalResourceSelectionList<T extends { id: number }>({
  title,
  titleExtra,
  searchLabel,
  searchPlaceholder,
  items,
  selectedId,
  keyword,
  loading,
  error,
  emptyText,
  total,
  hasMore,
  onLoadMore,
  resetKey,
  getLabel,
  renderPrimary,
  renderSecondary,
  onKeywordChange,
  onSearch,
  onRetry,
  onSelect
}: RentalResourceSelectionListProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [resetKey])

  useEffect(() => {
    const root = scrollRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel || loading || error || !hasMore) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore()
        }
      },
      { root }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loading, error, hasMore, onLoadMore])

  return (
    <section
      aria-label={`${title}列表`}
      className="border-border bg-surface flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border"
    >
      <div className="border-border flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3">
        <h3 className="text-content m-0 text-sm font-semibold">{title}</h3>
        <div className="flex items-center gap-3">
          {total > 0 ? <span className="text-content-soft text-xs">{total} 条</span> : null}
          {titleExtra}
        </div>
      </div>

      <form
        className="flex shrink-0 gap-2 p-3"
        onSubmit={(event) => {
          event.preventDefault()
          onSearch()
        }}
      >
        <label className="sr-only" htmlFor={`rental-resource-search-${title}`}>
          {searchLabel}
        </label>
        <input
          aria-label={searchLabel}
          className="border-border-strong bg-surface text-content placeholder:text-content-soft focus:border-accent focus:ring-accent/20 min-w-0 flex-1 rounded border px-3 py-1.5 text-sm transition outline-none focus:ring-2"
          id={`rental-resource-search-${title}`}
          onChange={(event) => onKeywordChange(event.target.value)}
          placeholder={searchPlaceholder}
          type="search"
          value={keyword}
        />
        <button
          className="border-border-strong text-content hover:bg-surface-muted rounded border px-3 py-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading}
          type="submit"
        >
          搜索
        </button>
      </form>

      {error ? (
        <div
          className="mx-3 mb-3 flex shrink-0 items-center justify-between gap-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          <span>{error}</span>
          {onRetry ? (
            <button className="shrink-0 underline underline-offset-2" onClick={onRetry} type="button">
              重试
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        aria-busy={loading}
        aria-label={`选择${title}`}
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain px-3 pb-3"
        ref={scrollRef}
        tabIndex={0}
        role="group"
      >
        {loading && items.length === 0 ? (
          <div aria-label={`正在加载${title}`} className="space-y-2" role="status">
            {[0, 1, 2].map((item) => (
              <div className="bg-surface-muted h-14 animate-pulse rounded-md" key={item} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-content-soft flex flex-1 items-center justify-center py-10 text-sm">{emptyText}</div>
        ) : (
          items.map((item) => {
            const selected = item.id === selectedId
            return (
              <button
                aria-label={getLabel(item)}
                aria-pressed={selected}
                className={cn(
                  'focus-visible:ring-accent/40 flex w-full shrink-0 items-start gap-3 rounded-md border px-3 py-2.5 text-left transition focus-visible:ring-2 focus-visible:outline-none',
                  selected
                    ? 'border-accent bg-[var(--accent-soft)]'
                    : 'hover:border-border hover:bg-surface-muted border-transparent'
                )}
                key={item.id}
                onClick={() => onSelect(item)}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border text-xs',
                    selected ? 'border-accent bg-accent text-accent-contrast font-semibold' : 'border-border-strong'
                  )}
                >
                  {selected ? '✓' : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="text-content block truncate text-sm font-medium">{renderPrimary(item)}</span>
                  {renderSecondary ? (
                    <span className="text-content-muted mt-1 block text-xs leading-5 break-words">
                      {renderSecondary(item)}
                    </span>
                  ) : null}
                </span>
              </button>
            )
          })
        )}
        {loading && items.length > 0 ? (
          <div className="text-content-soft px-1 text-xs" role="status">
            正在加载更多…
          </div>
        ) : null}
        <div className="text-content-soft shrink-0 text-center text-xs" ref={sentinelRef}>
          {!loading && !error && items.length > 0 ? (
            hasMore ? (
              <button onClick={onLoadMore} type="button" className="py-2 underline underline-offset-2">
                加载更多
              </button>
            ) : (
              <span role="status">已加载全部 {total} 条</span>
            )
          ) : null}
        </div>
      </div>
    </section>
  )
}
